import React, { useEffect, useRef, useState } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ContactForm from "../components/ContactForm";
import useSmoothScroll from "../hooks/useSmoothScroll";
import useScrollFx from "../hooks/useScrollFx";
import { SITE } from "../data/siteConfig";
import "../css/Contact.css";
import "../css/ContactFx.css";
import "../css/ContactDecor.css";

/* ---------- Workshop hours for the hanging sign (EDIT THESE) ----------
   Used only to decide OPEN / CLOSED. The text shown under the sign still
   comes from SITE.hours. Times are in Indian Standard Time, 24-hour. */
const OPEN_FROM = 9;
const OPEN_TO = 19;
const CLOSED_DAYS = ["Sun"];

function useOpenNow() {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const check = () => {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        hour: "numeric",
        hour12: false,
      }).formatToParts(new Date());
      const day = parts.find((p) => p.type === "weekday").value;
      const hr = parseInt(parts.find((p) => p.type === "hour").value, 10) % 24;
      setOpen(!CLOSED_DAYS.includes(day) && hr >= OPEN_FROM && hr < OPEN_TO);
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, []);
  return open;
}

function OpenSign() {
  const open = useOpenNow();
  if (open === null) return null;
  return (
    <div
      className={`ct-sign ${open ? "is-open" : "is-closed"}`}
      aria-live="polite"
    >
      <i className="ct-sign__string ct-sign__string--l" />
      <i className="ct-sign__string ct-sign__string--r" />
      <div className="ct-sign__board">
        <strong>{open ? "OPEN" : "CLOSED"}</strong>
        <span>{open ? "Visit the workshop" : "Message us anytime"}</span>
      </div>
    </div>
  );
}

const INFO_ITEMS = [
  {
    label: "Address",
    value: `Anappuram Wooden Furniture Work, ${SITE.address}`,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <circle
          cx="12"
          cy="9"
          r="2.4"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    ),
  },
  {
    label: "Working Hours",
    value: SITE.hours,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M12 7v5l3.5 2"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    label: "Call",
    value: SITE.phone,
    href: SITE.phoneLink,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L14 13l5 2v4a2 2 0 0 1-2 2C9.5 21 3 14.5 3 6a2 2 0 0 1 1-2z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "WhatsApp",
    value: SITE.whatsapp,
    href: SITE.whatsappLink,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.2 13.9c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7-2.8-1.1-4.6-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.8s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.9 2.1c.1.2.1.4 0 .5l-.3.5-.4.4c-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1l.9-1.1c.2-.3.4-.2.6-.1l2 1c.3.1.5.2.5.3.1.2.1.8-.1 1.4z" />
      </svg>
    ),
  },
  {
    label: "Service Area",
    value: "Orders accepted from all over India",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"
          stroke="currentColor"
          strokeWidth="1.4"
        />
      </svg>
    ),
  },
];

export default function Contact() {
  const root = useRef(null);
  useSmoothScroll();

  useScrollFx(root, (gsap) => {
    gsap.registerPlugin(ScrollTrigger);
    const scope = root.current;

    // Hero intro
    const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
    tl.from(".ct-hero__img", { scale: 1.35, duration: 2, ease: "power3.out" })
      .from(
        ".ct-hero .hero__eyebrow",
        { y: 20, opacity: 0, duration: 0.8 },
        0.3,
      )
      .from(".ct-line > span", { yPercent: 115, duration: 1.2 }, 0.4)
      .from(".ct-hero p", { y: 30, opacity: 0, duration: 0.9 }, 0.9);

    // Hero parallax
    const st = {
      trigger: ".ct-hero",
      start: "top top",
      end: "bottom top",
      scrub: true,
    };
    gsap.to(".ct-hero__img", {
      yPercent: 22,
      scale: 1.18,
      ease: "none",
      scrollTrigger: st,
    });
    gsap.to(".ct-hero__inner", {
      yPercent: -20,
      opacity: 0,
      ease: "none",
      scrollTrigger: st,
    });

    // Drafting sketch: the chair is "drawn" line by line, then measured
    const lines = scope.querySelectorAll(".ct-draw");
    lines.forEach((p) => {
      const len = p.getTotalLength();
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
    });
    gsap.set(".ct-dim", { opacity: 0 });
    gsap
      .timeline({ delay: 0.8 })
      .to(lines, {
        strokeDashoffset: 0,
        duration: 1.5,
        stagger: 0.22,
        ease: "power2.inOut",
      })
      .to(".ct-dim", { opacity: 1, duration: 0.8, stagger: 0.2 }, "-=0.6");

    // Form fields cascade
    gsap.from(
      ".contact-form__field, .contact-form > .btn, .contact-form > button",
      {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.09,
        ease: "power3.out",
        clearProps: "transform,opacity",
        scrollTrigger: {
          trigger: ".contact-form",
          start: "top 85%",
          once: true,
        },
      },
    );

    // Info rows: icon pops
    gsap.from(".contact-info-list__icon", {
      scale: 0,
      rotate: -180,
      duration: 0.8,
      stagger: 0.12,
      ease: "back.out(1.8)",
      clearProps: "transform",
      scrollTrigger: {
        trigger: ".contact-info-list",
        start: "top 85%",
        once: true,
      },
    });

    // Action buttons
    gsap.from(".contact-info-card__actions .btn", {
      y: 30,
      opacity: 0,
      duration: 0.7,
      stagger: 0.12,
      ease: "power3.out",
      clearProps: "transform,opacity",
      scrollTrigger: {
        trigger: ".contact-info-card__actions",
        start: "top 92%",
        once: true,
      },
    });

    // Map pin drops onto the frame
    gsap.from(".ct-pin", {
      y: -110,
      opacity: 0,
      duration: 1.1,
      ease: "bounce.out",
      scrollTrigger: { trigger: ".ct-map-wrap", start: "top 85%", once: true },
    });

    // Tape-measure progress bar
    gsap.to(".scroll-progress", {
      scaleX: 1,
      ease: "none",
      scrollTrigger: { start: 0, end: "max", scrub: 0.3 },
    });
  });

  return (
    <div ref={root}>
      <div className="scroll-progress" aria-hidden="true" />

      <section className="page-hero ct-hero">
        <img
          className="ct-hero__img"
          src="/assets/1.png"
          alt=""
          loading="eager"
        />
        <div className="ct-hero__overlay" />
        <div className="ct-hero__grid" aria-hidden="true" />

        {/* Drafting sketch of a chair with dimension lines */}
        <svg
          className="ct-sketch"
          viewBox="0 0 380 310"
          fill="none"
          aria-hidden="true"
        >
          <path className="ct-draw" d="M100 250 L116 30" />
          <path className="ct-draw" d="M116 30 H142" />
          <path className="ct-draw" d="M110 92 H138" />
          <path className="ct-draw" d="M104 170 H272 V188 H102 Z" />
          <path className="ct-draw" d="M262 188 V250" />
          <path className="ct-draw" d="M106 222 H262" />
          <path className="ct-draw" d="M92 250 H122 M250 250 H276" />
          {/* dimensions */}
          <g className="ct-dim">
            <path d="M326 30 V250 M320 30 H332 M320 250 H332" />
            <text x="338" y="146">
              90 cm
            </text>
          </g>
          <g className="ct-dim">
            <path d="M102 282 H272 M102 276 V288 M272 276 V288" />
            <text x="150" y="304">
              45 cm
            </text>
          </g>
        </svg>

        <div className="container ct-hero__inner">
          <span className="hero__eyebrow">Get In Touch</span>
          <h1>
            <span className="ct-line">
              <span>Contact Us</span>
            </span>
          </h1>
          <p>
            Visit our workshop, or call or WhatsApp us from anywhere
            in India. We're happy to discuss your requirements.
          </p>
        </div>
      </section>

      <section className="section ct-section ct-pegboard">
        <span className="ct-bigword" data-drift="-16" aria-hidden="true">
          Say Hello · Say Hello
        </span>
        <div className="container contact-grid">
          {/* Job card pinned to the bench */}
          <div className="contact-grid__form ct-form" data-reveal="left">
            <span className="ct-tape" aria-hidden="true" />
            <h2>Send Us a Message</h2>
            <p className="contact-grid__form-lead">
              Tell us about your project. Your message opens in WhatsApp so you
              can send it to us directly.
            </p>
            <ContactForm />
          </div>

          <div className="contact-grid__info" data-reveal="right">
            <div className="contact-info-card">
              <OpenSign />
              <h2>Business Information</h2>
              <ul className="contact-info-list">
                {INFO_ITEMS.map((item) => (
                  <li key={item.label}>
                    <span className="contact-info-list__icon">{item.icon}</span>
                    <div>
                      <strong>{item.label}</strong>
                      {item.href ? (
                        <a href={item.href}>{item.value}</a>
                      ) : (
                        <span>{item.value}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="contact-info-card__actions">
                <a href={SITE.phoneLink} className="btn">
                  Call Now
                </a>
                <a
                  href={SITE.whatsappLink}
                  className="btn btn--outline"
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>

            <div className="ct-map-wrap">
              <span className="ct-pin" aria-hidden="true">
                <svg width="34" height="44" viewBox="0 0 34 44">
                  <path
                    d="M17 43s13-14 13-26A13 13 0 0 0 4 17c0 12 13 26 13 26z"
                    fill="#b3402a"
                    stroke="#6e1f10"
                    strokeWidth="1.5"
                  />
                  <circle cx="17" cy="17" r="5" fill="#fbe9c4" />
                </svg>
              </span>
              <div className="contact-map" data-clip-up>
                <iframe
                  title="Anappuram Wooden Furniture Work location map"
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d355.3568159036627!2d76.65590150533725!3d10.72218431714637!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTDCsDQzJzIwLjMiTiA3NsKwMzknMjEuNCJF!5e1!3m2!1sen!2sin!4v1790827667004!5m2!1sen!2sin"
                  width="600"
                  height="450"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <a
        className="ct-whatsapp"
        href={SITE.whatsappLink}
        target="_blank"
        rel="noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.2 13.9c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7-2.8-1.1-4.6-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.8s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.9 2.1c.1.2.1.4 0 .5l-.3.5-.4.4c-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1l.9-1.1c.2-.3.4-.2.6-.1l2 1c.3.1.5.2.5.3.1.2.1.8-.1 1.4z" />
        </svg>
      </a>
    </div>
  );
}
